/**
 * Row-block view — the Databento catalogue shape: name and category on the
 * left, a navigational affordance on the right, then a description line and a
 * strip of factual chips.
 *
 * Every chip is derived from the registry, including the sample-constituent
 * chips, which render only for the datasets whose SDK pull was unfiltered and
 * therefore surfaced genuine tickers. Datasets without `sampleTickers` show no
 * constituent strip at all — an absent strip is honest, an invented one is not.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Dataset } from 'app/data/datasets';
import { formatEntities, shortUniverse, sinceYear } from './coverage';
import {
  Chip,
  ConstituentChips,
  CoverageMeter,
  NotDocumented,
  StatusChip,
} from './ui';

function DatasetRow({ dataset }: { dataset: Dataset }) {
  const since = sinceYear(dataset.historyStart);

  return (
    <li className="border-b border-dashed border-gray-800/70 px-2.5 py-1.5 last:border-b-0 hover:bg-gray-900/60 focus-within:bg-gray-900/60">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="text-[13px]">
          <Link
            to={`/datasets/${dataset.slug}`}
            className="text-white underline decoration-gray-700 underline-offset-2 hover:text-blue-300 hover:decoration-blue-400 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
          >
            {dataset.name}
          </Link>
          <span className="ml-2 font-mono text-[11px] text-gray-400">
            {dataset.category}
          </span>
        </h3>
        <Link
          to={`/datasets/${dataset.slug}`}
          tabIndex={-1}
          aria-hidden="true"
          className="shrink-0 font-mono text-[11px] text-gray-400 hover:text-blue-300"
        >
          coverage &amp; schema →
        </Link>
      </div>

      <p className="max-w-3xl text-[11px] leading-snug text-gray-400">
        {dataset.summary}
      </p>

      <div className="mt-1 flex flex-wrap items-center gap-1">
        {since ? (
          <Chip title={`Earliest observation ${dataset.historyStart}`}>
            since {since}
          </Chip>
        ) : (
          <Chip title="History start has not been published yet">
            history n/d
          </Chip>
        )}
        {dataset.entities === undefined ? (
          <Chip title="Entity count has not been published yet">
            entities n/d
          </Chip>
        ) : (
          <Chip title="Approximate distinct entities covered">
            {formatEntities(dataset.entities)} entities
          </Chip>
        )}
        {dataset.universe ? (
          <Chip
            tone={dataset.universe.includes('private') ? 'broad' : 'default'}
            title={dataset.universe}
          >
            {shortUniverse(dataset.universe)}
          </Chip>
        ) : (
          <Chip title="Universe has not been published yet">universe n/d</Chip>
        )}
        <StatusChip status={dataset.status} />
        <ConstituentChips tickers={dataset.sampleTickers} limit={6} />
        <span className="ml-auto flex items-center gap-2">
          <CoverageMeter dataset={dataset} />
        </span>
      </div>

      {/* Full "not yet documented" wording, once per row, for the gaps above. */}
      {(!since || dataset.entities === undefined || !dataset.universe) && (
        <p className="mt-1 font-mono text-[11px] text-gray-400">
          <NotDocumented />
          {': '}
          {[
            !since ? 'history start' : null,
            dataset.entities === undefined ? 'entity count' : null,
            !dataset.universe ? 'universe' : null,
          ]
            .filter(Boolean)
            .join(', ')}
        </p>
      )}
    </li>
  );
}

export function DatasetRows({ rows }: { rows: Dataset[] }) {
  return (
    <div className="border border-gray-800">
      <ul className="divide-y-0">
        {rows.map(dataset => (
          <DatasetRow key={dataset.slug} dataset={dataset} />
        ))}
      </ul>
    </div>
  );
}

export default DatasetRows;
